import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import ActionDropdown from '../../../components/common/action-dropdown';
import StrategyCard from '../../../components/trading/StrategyCard';
import { Plus, Filter, Users } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import TradingStatusBadge from '../../../components/trading/TradingStatusBadge';
import RiskScoreBadge from '../../../components/trading/RiskScoreBadge';

type Strategy = {
  id: string;
  name: string;
  manager: string;
  managerId: string;
  roi: number;
  totalProfit: number;
  totalInvested: number;
  investors: number;
  riskLevel: 'low' | 'medium' | 'high';
  riskScore: number;
  status: 'active' | 'inactive' | 'pending';
  createdAt: string;
  minInvestment: number;
  maxInvestment: number;
  commissionRate: number;
};

const PAMMStrategies = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');

  const strategies: Strategy[] = [
    {
      id: 'STR001',
      name: 'Gold Scalper Pro',
      manager: 'John Smith',
      managerId: 'MGR001',
      roi: 45.5,
      totalProfit: 56875,
      totalInvested: 125000,
      investors: 23,
      riskLevel: 'high',
      riskScore: 8,
      status: 'active',
      createdAt: '2024-01-15',
      minInvestment: 1000,
      maxInvestment: 50000,
      commissionRate: 20,
    },
    {
      id: 'STR002',
      name: 'Forex Trend Master',
      manager: 'Jane Doe',
      managerId: 'MGR002',
      roi: 32.8,
      totalProfit: 32144,
      totalInvested: 98000,
      investors: 18,
      riskLevel: 'medium',
      riskScore: 5,
      status: 'active',
      createdAt: '2024-01-10',
      minInvestment: 500,
      maxInvestment: 100000,
      commissionRate: 15,
    },
    {
      id: 'STR003',
      name: 'Conservative Growth',
      manager: 'Bob Johnson',
      managerId: 'MGR003',
      roi: 15.2,
      totalProfit: 11400,
      totalInvested: 75000,
      investors: 12,
      riskLevel: 'low',
      riskScore: 3,
      status: 'pending',
      createdAt: '2024-01-08',
      minInvestment: 250,
      maxInvestment: 25000,
      commissionRate: 10,
    },
  ];

  const columns: DataTableColumn<Strategy>[] = [
    {
      id: 'id',
      header: 'Strategy ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '120px',
    },
    {
      id: 'name',
      header: 'Strategy Name',
      accessor: row => row.name,
      sortable: true,
      sortValue: row => row.name,
      width: '180px',
    },
    {
      id: 'manager',
      header: 'Manager',
      accessor: row => row.manager,
      sortable: true,
      sortValue: row => row.manager,
      width: '150px',
    },
    {
      id: 'roi',
      header: 'ROI',
      accessor: row => (
        <span className="text-green-600 font-semibold">
          {row.roi.toFixed(2)}%
        </span>
      ),
      sortable: true,
      sortValue: row => row.roi,
      width: '100px',
    },
    {
      id: 'totalInvested',
      header: 'Total Invested',
      accessor: row => `$${row.totalInvested.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.totalInvested,
      width: '140px',
    },
    {
      id: 'investors',
      header: 'Investors',
      accessor: row => (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          <span>{row.investors}</span>
        </div>
      ),
      sortable: true,
      sortValue: row => row.investors,
      width: '120px',
    },
    {
      id: 'riskLevel',
      header: 'Risk',
      accessor: row => <RiskScoreBadge score={row.riskScore} />,
      sortable: true,
      sortValue: row => row.riskScore,
      width: '140px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <TradingStatusBadge status={row.status} />,
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          onView={() => console.log('View strategy:', row.id)}
          onEdit={() => console.log('Edit strategy:', row.id)}
          onDelete={() => console.log('Delete strategy:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  const filteredStrategies = strategies.filter(strategy => {
    const matchesSearch =
      searchTerm === '' ||
      strategy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      strategy.manager.toLowerCase().includes(searchTerm.toLowerCase()) ||
      strategy.id.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Strategy Management"
          description="Manage PAMM strategies and master accounts"
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'table' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('table')}
          >
            Table View
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            Grid View
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            New Strategy
          </Button>
        </div>
      </div>

      {viewMode === 'table' ? (
        <Card>
          <CardHeader>
            <CardTitle>All Strategies</CardTitle>
            <CardDescription>
              Manage and monitor all PAMM strategies
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={filteredStrategies}
              searchPlaceholder="Search by strategy name, manager, or ID..."
              onSearchChange={setSearchTerm}
              showColumnVisibility={true}
              showExportButton={true}
              showAddButton={false}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredStrategies.map(strategy => (
            <StrategyCard
              key={strategy.id}
              id={strategy.id}
              name={strategy.name}
              manager={strategy.manager}
              roi={strategy.roi}
              totalProfit={strategy.totalProfit}
              totalInvested={strategy.totalInvested}
              investors={strategy.investors}
              riskLevel={strategy.riskLevel}
              status={strategy.status}
              onView={() => console.log('View:', strategy.id)}
              onInvest={() => console.log('Invest:', strategy.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PAMMStrategies;
